import { useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  arraySwap,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import type { FileRejection } from 'react-dropzone';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { AdMediaType } from '../../types/ads';
import { FileKindEnum } from '../../types/enums';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';

import { FileUploader } from '../uiComponents/FileUploader';
import { EditorSection } from './EditorSection';

export const PROPERTY_AD_PHOTO_LIMIT = 50;
export const PROPERTY_AD_PHOTO_MAX_SIZE_BYTES = 15 * 1024 * 1024;
export const PROPERTY_AD_PHOTO_ACCEPT = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

interface GalleryPhoto {
  fileId: string;
  order: number;
  url: string;
}

interface SortablePhotoProps {
  disabled: boolean;
  onDelete: (fileId: string) => void;
  onSetCover: (fileId: string) => void;
  photo: GalleryPhoto;
}

interface UploadPhotoSectionProps {
  disabled?: boolean;
  onMediaChanged?: (media: AdMediaType[]) => void;
  onUploadComplete?: (ok: boolean) => void;
  onUploadStart?: () => void;
  persistPhotoMedia?: (
    photos: AdMediaType[]
  ) => ReturnType<typeof propertyAdEditorProvider.updateInProgressMedia>;
  publishError?: string;
  workspace: Pick<PropertyAdInProgressWorkspace, 'id' | 'propertyAdMedia'>;
}

const getWorkspacePhotos = (media: AdMediaType[] | null | undefined): GalleryPhoto[] =>
  (media ?? [])
    .filter(
      (item): item is AdMediaType & { fileId: string; fileUrl: string } =>
        item.kind === FileKindEnum.Image && Boolean(item.fileId) && Boolean(item.fileUrl)
    )
    .sort((first, second) => first.order - second.order)
    .map(({ fileId, fileUrl }, index) => ({ fileId, order: index + 1, url: fileUrl }));

const getNonPhotoMedia = (media: AdMediaType[] | null | undefined): AdMediaType[] =>
  (media ?? [])
    .filter((item) => item.kind !== FileKindEnum.Image)
    .map(({ fileId, kind, mediaType, order }) => ({ fileId, kind, mediaType, order }));

const getMediaUpdate = (nonPhotoMedia: AdMediaType[], photos: GalleryPhoto[]): AdMediaType[] => [
  ...nonPhotoMedia,
  ...photos.map(({ fileId }, index) => ({
    fileId,
    kind: FileKindEnum.Image,
    order: index + 1,
  })),
];

const getPhotoMedia = (photos: GalleryPhoto[]): AdMediaType[] =>
  photos.map(({ fileId, url }, index) => ({
    fileId,
    fileUrl: url,
    kind: FileKindEnum.Image,
    order: index + 1,
  }));

const getConfirmedPhotos = (
  responseMedia: AdMediaType[] | null | undefined,
  requestedPhotos: GalleryPhoto[]
): GalleryPhoto[] => {
  if (!Array.isArray(responseMedia)) {
    return requestedPhotos;
  }

  const requestedPhotoById = new Map(
    requestedPhotos.map((photo) => [photo.fileId, photo] as const)
  );
  const confirmedPhotos = responseMedia
    .filter(
      (item): item is AdMediaType & { fileId: string } =>
        item.kind === FileKindEnum.Image && Boolean(item.fileId)
    )
    .sort((first, second) => first.order - second.order)
    .map((item, index) => ({
      fileId: item.fileId,
      order: index + 1,
      url: item.fileUrl ?? requestedPhotoById.get(item.fileId)?.url ?? '',
    }));

  return confirmedPhotos.every(({ url }) => Boolean(url)) ? confirmedPhotos : requestedPhotos;
};

const SortablePhoto = ({ disabled, onDelete, onSetCover, photo }: SortablePhotoProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: photo.fileId,
  });

  return (
    <Box
      aria-label={`Photo ${photo.order}${photo.order === 1 ? ', cover' : ''}`}
      component='article'
      ref={setNodeRef}
      sx={{
        backgroundColor: '#f7fafc',
        border: '1px solid #dce9f7',
        borderRadius: 2,
        minWidth: 0,
        overflow: 'hidden',
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Box
        alt={`Listing photo ${photo.order}`}
        component='img'
        src={photo.url}
        sx={{
          aspectRatio: '4 / 3',
          display: 'block',
          objectFit: 'cover',
          width: '100%',
        }}
      />
      <Stack alignItems='center' direction='row' justifyContent='space-between' padding={1}>
        <Stack alignItems='center' direction='row' minWidth={0} spacing={0.5}>
          <IconButton
            {...attributes}
            {...listeners}
            aria-label={`Reorder photo ${photo.order}`}
            disabled={disabled}
            size='small'
            sx={{ cursor: disabled ? 'default' : 'grab' }}
          >
            <DragIndicatorIcon fontSize='small' />
          </IconButton>
          {photo.order === 1 ? (
            <Chip color='primary' label='Cover' size='small' />
          ) : (
            <Button
              disabled={disabled}
              onClick={() => onSetCover(photo.fileId)}
              size='small'
              sx={{ minWidth: 0, textTransform: 'none', whiteSpace: 'nowrap' }}
              type='button'
              variant='text'
            >
              Set as cover
            </Button>
          )}
        </Stack>
        <IconButton
          aria-label={`Delete photo ${photo.order}`}
          disabled={disabled}
          onClick={() => onDelete(photo.fileId)}
          size='small'
        >
          <DeleteOutlineIcon fontSize='small' />
        </IconButton>
      </Stack>
    </Box>
  );
};

export const UploadPhotoSection = ({
  disabled = false,
  onMediaChanged,
  onUploadComplete,
  onUploadStart,
  persistPhotoMedia,
  publishError,
  workspace,
}: UploadPhotoSectionProps) => {
  const [photos, setPhotos] = useState<GalleryPhoto[]>(() =>
    getWorkspacePhotos(workspace.propertyAdMedia)
  );
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const photosRef = useRef(photos);
  const nonPhotoMedia = useMemo(
    () => getNonPhotoMedia(workspace.propertyAdMedia),
    [workspace.propertyAdMedia]
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const hydratedPhotos = getWorkspacePhotos(workspace.propertyAdMedia);
    photosRef.current = hydratedPhotos;
    setPhotos(hydratedPhotos);
    setError('');
  }, [workspace.id, workspace.propertyAdMedia]);

  const persistPhotos = async (nextPhotos: GalleryPhoto[]) => {
    setError('');
    setIsSaving(true);

    try {
      const response = persistPhotoMedia
        ? await persistPhotoMedia(getPhotoMedia(nextPhotos))
        : await propertyAdEditorProvider.updateInProgressMedia(workspace.id, {
            propertyAdMedia: getMediaUpdate(nonPhotoMedia, nextPhotos),
          });

      if (!response.ok) {
        setError(response.message || "Couldn't save photo changes. Please try again.");
        return false;
      }

      const confirmedPhotos = getConfirmedPhotos(response.data?.propertyAdMedia, nextPhotos);
      const normalizedPhotos = confirmedPhotos.map((photo, index) => ({
        ...photo,
        order: index + 1,
      }));
      photosRef.current = normalizedPhotos;
      setPhotos(normalizedPhotos);
      if (!persistPhotoMedia) {
        onMediaChanged?.([
          ...(workspace.propertyAdMedia ?? []).filter((item) => item.kind !== FileKindEnum.Image),
          ...normalizedPhotos.map(({ fileId, order, url }) => ({
            fileId,
            fileUrl: url,
            kind: FileKindEnum.Image,
            order,
          })),
        ]);
      }
      return true;
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Couldn't save photo changes. Please try again."
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUploaded = async (fileId: string, url: string) => {
    const nextPhotos = [...photosRef.current, { fileId, order: photosRef.current.length + 1, url }];
    return persistPhotos(nextPhotos);
  };

  const handleDelete = (fileId: string) => {
    const nextPhotos = photosRef.current.filter((photo) => photo.fileId !== fileId);
    void persistPhotos(nextPhotos);
  };

  const handleSetCover = (fileId: string) => {
    const photoIndex = photosRef.current.findIndex((photo) => photo.fileId === fileId);
    if (photoIndex <= 0) return;

    void persistPhotos(arraySwap(photosRef.current, 0, photoIndex));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (disabled || !over || active.id === over.id) return;

    const activeIndex = photosRef.current.findIndex((photo) => photo.fileId === active.id);
    const overIndex = photosRef.current.findIndex((photo) => photo.fileId === over.id);
    if (activeIndex < 0 || overIndex < 0) return;

    void persistPhotos(arrayMove(photosRef.current, activeIndex, overIndex));
  };

  const handleRejectedPhotos = (rejections: FileRejection[]) => {
    const hasOversizedPhoto = rejections.some(({ errors }) =>
      errors.some(({ code }) => code === 'file-too-large')
    );
    setError(
      hasOversizedPhoto
        ? 'Each photo must be 15 MB or smaller.'
        : 'Only JPG, JPEG or PNG files are allowed.'
    );
  };

  return (
    <EditorSection
      cardTestId='upload-photo-card'
      description='Add at least four photos to create a comprehensive listing.'
      required
      title='Upload photo'
      titleId='upload-photo-title'
    >
      <Stack minWidth={0} spacing={3}>
        {photos.length > 0 && (
          <Box
            aria-label='Listing photos'
            component='section'
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              minWidth: 0,
            }}
          >
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <SortableContext
                items={photos.map(({ fileId }) => fileId)}
                strategy={rectSortingStrategy}
              >
                {photos.map((photo) => (
                  <SortablePhoto
                    disabled={disabled || isSaving}
                    key={photo.fileId}
                    onDelete={handleDelete}
                    onSetCover={handleSetCover}
                    photo={photo}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        )}

        <FileUploader
          accept={PROPERTY_AD_PHOTO_ACCEPT}
          awaitOnUploaded
          disabled={disabled || isSaving || photos.length >= PROPERTY_AD_PHOTO_LIMIT}
          fullWidth
          labelButton='Upload photo'
          labelFormat='Only JPG, JPEG or PNG format, minimum size 800 x 600px'
          labelSize='Maximum 15 MB one photo'
          limit={PROPERTY_AD_PHOTO_LIMIT - photos.length}
          maxSize={PROPERTY_AD_PHOTO_MAX_SIZE_BYTES}
          onRejected={handleRejectedPhotos}
          onUploadComplete={onUploadComplete}
          onUploadStart={onUploadStart}
          onUploaded={handlePhotoUploaded}
          type='image'
        />

        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          spacing={1}
        >
          <Typography color='#6f7d87' component='p' variant='body2'>
            {photos.length} of {PROPERTY_AD_PHOTO_LIMIT} photos
          </Typography>
          {isSaving && (
            <Typography
              aria-live='polite'
              color='#6f7d87'
              component='p'
              role='status'
              variant='body2'
            >
              Saving photo changes...
            </Typography>
          )}
        </Stack>
        {publishError && (
          <Typography
            color='error'
            component='p'
            id='upload-photo-publish-error'
            role='alert'
            variant='body2'
          >
            {publishError}
          </Typography>
        )}
        {error && (
          <Typography color='error' component='p' role='alert' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </EditorSection>
  );
};
